/**
 * **What to call one member wherever a family chooses between them** (PR #336 review; here
 * since issue #387).
 *
 * Two active members may share a display name, which the service supports deliberately: #92
 * compares subjects by `memberId` for exactly this reason. Rendering the name alone gives two
 * different people the same label, and the cost lands wherever a person is *chosen* — a
 * durable fact assigned to the wrong Sam, an item handed to the wrong Sam, or a filter chip
 * whose twin reveals somebody else's rows.
 *
 * It was `apps/my.iamboone.app/src/members.ts`'s — one function because the panel had grown
 * three copies — and it is here now because a second surface has the same pickers. A rule
 * about identity that two surfaces apply differently is worse than two copies of a heading:
 * the picker on the phone would name the wrong Sam where the panel named the right one.
 *
 * **Structural on purpose.** It takes any row with a `name` that may be absent and an `email`
 * that is not, which is what `GET /roster` answers, so this package stays free of the route's
 * derived type and the two surfaces still call it on the same rows.
 */
export interface LabelledMember {
  readonly name?: string | undefined;
  readonly email: string;
}

export function memberLabel(member: LabelledMember, members: readonly LabelledMember[]): string {
  // **A member may have no name.** `name` comes from a `Map.get` over the roster on the server
  // and the type says so; the address is the fallback because it is the one field that is
  // always present and still identifies the person. A neutral word like "a member" would be
  // calmer and useless: this label's whole job is telling two people apart.
  const name = member.name;
  if (name === undefined) return member.email;
  const shared = members.filter((other) => other.name === name).length > 1;
  return shared ? `${name} (${member.email})` : name;
}
